import { WORKER_BASE_URL } from './env'

const SUBSET_FONT_API_URL = WORKER_BASE_URL + '/subset-font'
const DEFAULT_FONT_ID = 'wenkai-v1'
const DEFAULT_FORMAT = 'ttf'

const SYSTEM_FONT_FALLBACK = '"LXGW WenKai", "STKaiti", "KaiTi", "Kaiti SC", "楷体", serif'

let activeFontFamily = SYSTEM_FONT_FALLBACK
let activeFontHash = ''
let activeFontChars = ''
let fontLoadPromise: Promise<string> | null = null

export interface SubsetFontResult {
  family: string
  source: string
  format: string
  hash: string
}

export function getActiveFontFamily(): string {
  return activeFontFamily
}

export function getActiveFontHash(): string {
  return activeFontHash
}

export function resetToSystemFont(): void {
  activeFontFamily = SYSTEM_FONT_FALLBACK
  activeFontHash = ''
  activeFontChars = ''
  fontLoadPromise = null
}

export async function loadSubsetFont(chars: string): Promise<string> {
  const normalized = normalizeChars(chars)
  if (!normalized) {
    resetToSystemFont()
    return activeFontFamily
  }

  if (normalized === activeFontChars && activeFontFamily !== SYSTEM_FONT_FALLBACK) {
    return activeFontFamily
  }

  if (fontLoadPromise) return fontLoadPromise

  fontLoadPromise = (async () => {
    try {
      const subsetFont = await requestSubsetFont(normalized)
      if (!subsetFont) {
        resetToSystemFont()
        return activeFontFamily
      }

      if (activeFontHash === subsetFont.hash && activeFontFamily !== SYSTEM_FONT_FALLBACK) {
        activeFontChars = normalized
        return activeFontFamily
      }

      const fontFace = new FontFace(subsetFont.family, subsetFont.source)
      await fontFace.load()
      document.fonts.add(fontFace)

      activeFontFamily = `"${subsetFont.family}", ${SYSTEM_FONT_FALLBACK}`
      activeFontHash = subsetFont.hash
      activeFontChars = normalized
      return activeFontFamily
    } catch (err) {
      console.warn('Font subset load failed, fallback to system font:', err)
      resetToSystemFont()
      return activeFontFamily
    } finally {
      fontLoadPromise = null
    }
  })()

  return fontLoadPromise
}

async function requestSubsetFont(chars: string): Promise<SubsetFontResult | null> {
  const payload = {
    text: chars,
    fontId: DEFAULT_FONT_ID,
    format: DEFAULT_FORMAT,
  }

  const response = await fetch(SUBSET_FONT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    console.warn('Subset font request failed:', response.status)
    return null
  }

  const data = await response.json()
  return normalizeSubsetFont(data)
}

function normalizeSubsetFont(data: unknown): SubsetFontResult | null {
  const source =
    data && typeof data === 'object' && (data as Record<string, unknown>).data && typeof (data as Record<string, unknown>).data === 'object'
      ? (data as Record<string, unknown>).data
      : data

  if (!source || typeof source !== 'object') return null
  const s = source as Record<string, unknown>
  if (!s.ok && !s.source) return null
  if (typeof s.family !== 'string' || !s.family) return null
  if (typeof s.source !== 'string' || !s.source) return null

  return {
    family: s.family,
    source: s.source,
    format: typeof s.format === 'string' ? s.format : DEFAULT_FORMAT,
    hash: typeof s.hash === 'string' ? s.hash : '',
  }
}

function normalizeChars(text: string): string {
  return Array.from(new Set(String(text || '').replace(/[^\u4e00-\u9fa5]/g, '').split(''))).join('')
}
